import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card, CardContent, Box, Typography, Button, Chip, Alert, CircularProgress,
} from '@mui/material';
import { Lock as LockIcon, Refresh as RefreshIcon, AutorenewOutlined } from '@mui/icons-material';
import { sslApi, SslStatus } from '../../../services/api/sslApi';

const colorForDays = (d: number): 'success' | 'warning' | 'error' => {
  if (d <= 7) return 'error';
  if (d <= 30) return 'warning';
  return 'success';
};

const SslStatusCard: React.FC = () => {
  const [status, setStatus] = useState<SslStatus | null>(null);
  const [offline, setOffline] = useState(false);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await sslApi.getStatus();
      if (!mountedRef.current) return;
      setStatus(s);
      setOffline(false);
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('[SslStatusCard] status fetch failed:', err);
      setOffline(true);
      setStatus(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon fontSize="small" /> SSL 인증서
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            새로고침
          </Button>
        </Box>

        {offline ? (
          <Alert severity="warning">상태 조회 불가 — 인증서 상태 서비스에 연결할 수 없습니다.</Alert>
        ) : loading && !status ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
        ) : status ? (
          <>
            <Typography color="text.secondary" gutterBottom>{status.domain}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Chip color={colorForDays(status.daysRemaining)} label={`${status.daysRemaining}일 남음`} />
              <Typography variant="body2">
                만료일: {new Date(status.notAfter).toLocaleDateString('ko-KR')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AutorenewOutlined fontSize="small" color={status.autoRenew ? 'success' : 'disabled'} />
              <Typography variant="caption" color="text.secondary">
                {status.autoRenew ? '자동 갱신 켜짐' : '자동 갱신 꺼짐'}
                {` · 다음 갱신 예정: ${new Date(status.nextRenewalApprox).toLocaleDateString('ko-KR')}`}
                {` · 발급자: ${status.issuer}`}
              </Typography>
            </Box>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default SslStatusCard;
